import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  IconButton,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SvgIcon,
} from "@mui/material";
import {
  Send,
  InsertDriveFile,
  Image,
  CheckCircle,
  AutoFixHigh,
  ExpandMore,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import { getChatHistory, submitRecruiterAnswer, getMissingFieldsStatus } from "./services";
import { useAddJobContext } from "./context/AddJobContext";
import { useMessagePopup } from "context/messagePopContext";

interface MissingField {
  completed: any;
  description: string;
  value: string;
  title?: string;
}

interface Message {
  id: number;
  text: string;
  sender: "AI" | "User";
  timestamp: string;
  delivered?: boolean;
  attachments?: File[];
  pinned?: boolean;
}

const Container = styled(Box)({
  display: "flex",
  gap: "24px",
  width: "99%",
  margin: "auto",
  overflow: "hidden",
  marginTop: "30px",
  });
  
  const SidePanel = styled(Card)(({ theme }) => ({
  flex: "0 0 380px",
  padding: "16px",
  borderRadius: "12px",
  background: "#ffffff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  border: "1px solid #82B3E6",
  height: "80vh",
  [theme.breakpoints.down("md")]: {
    flex: "none",
    width: "95%",
    margin: "0 auto",
    height: "auto",
    maxHeight: "400px",
  },
  }));
  
  const ChatBox = styled(Card)(({ theme }) => ({
  flex: 1,
  borderRadius: "12px",
  display: "flex",
  flexDirection: "column",
  background: "#fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  overflow: "hidden",
  position: "relative",
  height: "80vh",
  border: "1px solid #82B3E6",
  padding: "10px",
  [theme.breakpoints.down("md")]: {
    width: "95%",
    margin: "0 auto",
    height: "60vh",
    flex: "none",
  },
  }));
  const MessageList = styled(Box)({
  flexGrow: 1,
  overflowY: "auto",
  padding: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  });
  
  const ChatInputContainer = styled(Box)({
  position: "sticky",
  bottom: 0,
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "16px",
  borderTop: "1px solid #e0e0e0",
  background: "#fff",
  transition: "all 0.3s ease",
  });
  
  const DottedCircleIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <circle cx="12" cy="3" r="1.5" fill="#D3D8E0" />
    <circle cx="18" cy="5" r="1.5" fill="#D3D8E0" />
    <circle cx="21" cy="12" r="1.5" fill="#D3D8E0" />
    <circle cx="18" cy="19" r="1.5" fill="#D3D8E0" />
    <circle cx="12" cy="21" r="1.5" fill="#D3D8E0" />
    <circle cx="6" cy="19" r="1.5" fill="#D3D8E0" />
    <circle cx="3" cy="12" r="1.5" fill="#D3D8E0" />
    <circle cx="6" cy="5" r="1.5" fill="#D3D8E0" />
  </SvgIcon>
  );
  
  const ProDottedCircleIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <circle cx="12" cy="3" r="1.5" fill="#286BEF" />
    <circle cx="18" cy="5" r="1.5" fill="#286BEF" />
    <circle cx="21" cy="12" r="1.5" fill="#286BEF" />
    <circle cx="18" cy="19" r="1.5" fill="#286BEF" />
    <circle cx="12" cy="21" r="1.5" fill="#286BEF" />
    <circle cx="6" cy="19" r="1.5" fill="#286BEF" />
    <circle cx="3" cy="12" r="1.5" fill="#286BEF" />
    <circle cx="6" cy="5" r="1.5" fill="#286BEF" />
  </SvgIcon>
  );


const AIChat: React.FC<{ submitBtnRef: any }> = ({ submitBtnRef }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const [missingFields, setMissingFields] = useState<MissingField[]>([]);
  const { expertId, jobId, setLoading, handleSetJobId } = useAddJobContext();
  const message = useMessagePopup();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [chatRes, missingFieldRes] = await Promise.all([
          getChatHistory({ jobId, expertId }),
          getMissingFieldsStatus({ jobId, expertId })
        ]);

        const chatHistory = chatRes.output.jobRequirementChatDetails.missingRequirementChatHistory;
        const chatMessages = chatHistory.map((msg: any, idx: number) => ({
          id: idx + 1,
          text: msg.message,
          sender: msg.generatedBy === "SYSTEM" ? "AI" : "User",
          timestamp: new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          delivered: true
        }));

        setMessages(chatMessages);
        setMissingFields(missingFieldRes.output.jobChatMissingRequirementDetails || []);
      } catch (err) {
        console.error("Failed to fetch chat data", err);
      } finally {
        setLoading(false);
      }
    };

    if (jobId && expertId) {
      fetchAll();
    }
  }, [jobId, expertId]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() && files.length === 0) return;

    const newId = messages.length + 1;
    const userMessage: Message = {
      id: newId,
      text: inputValue,
      sender: "User",
      timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      attachments: [...files],
      delivered: false,
    };

    const typingMessage: Message = {
      id: newId + 1,
      text: "Typing...",
      sender: "AI",
      timestamp: userMessage.timestamp,
    };

    setMessages(prev => [...prev, userMessage, typingMessage]);
    setInputValue("");
    setFiles([]);

    try {
      const res = await submitRecruiterAnswer({ jobId, expertId, answer: inputValue });
      const updated = res?.output?.response;

      const newMessages = updated.map((msg: any, idx: number) => ({
        id: newId + idx + 1,
        text: msg.message,
        sender: msg.generatedBy === "SYSTEM" ? "AI" : "User",
        timestamp: new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        delivered: true,
      }));

      setMessages(prev => [...prev.filter(m => m.id !== typingMessage.id), ...newMessages]);

      const missingFieldRes = await getMissingFieldsStatus({ jobId, expertId })
      setMissingFields(missingFieldRes?.output?.jobChatMissingRequirementDetails || []);

    } catch (err) {
      console.error("Error sending message", err);
    }
  };

  const handleSubmitChat = () => {
    const hasIncomplete = missingFields.some((f) => !f.completed);
    if (hasIncomplete) {
      message.confirm("Some questions are unanswered. Continue anyway?", () => {
        setLoading(true);
        setTimeout(() => {
          setLoading(false);
          handleSetJobId(jobId, "reviewJobBar");
        }, 1000);
      });
    } else {
      handleSetJobId(jobId, "reviewJobBar");
    }
  };

  return (
    <Container>
      <SidePanel>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: "#000000",
            fontSize: "1.1rem",
          }}
        >
          Pro Tips for Better Job Listings
        </Typography>
        <Box
          sx={{
            mb: 3,
            background: "#F7F9FD",
            p: 2,
            border: "1px solid #286BEF",
            borderRadius: "8px",
          }}
        >
          <List dense>
            {[
              "Clear about top skills to filter strong candidates.",
              "Mention team size & work culture to attract the right fit.",
              "Share challenges faced in hiring for AI to suggest solutions.",
              "Specify visa requirements early to avoid mismatches.",
            ].map((tip, index) => (
              <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ProDottedCircleIcon sx={{ color: "#286BEF" }} />
                </ListItemIcon>
                <ListItemText
                  primary={tip}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontSize: "0.9rem", color: "#286BEF" },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Typography variant="h6" fontWeight={700} mt={3}>AI Questions Brief</Typography>
        <Box sx={{ height: "300px", overflowY: "auto" }}>
          {missingFields.length > 0 ? missingFields.map((field, i) => (
            field.completed ? (
              <Accordion key={i} disableGutters elevation={0}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircle sx={{ color: "#286BEF" }} /></ListItemIcon>
                  <Typography fontSize="0.9rem" color="#286BEF">{field.description}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography fontSize="0.8rem" color="#334155">{field.value}</Typography>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Box key={i} display="flex" alignItems="center" py={1}>
                <ListItemIcon sx={{ minWidth: 32 }}><DottedCircleIcon /></ListItemIcon>
                <Typography fontSize="0.9rem" color="#999">{field.description}</Typography>
              </Box>
            )
          )) : (
            <Typography fontSize="0.9rem" color="#999">No missing requirements.</Typography>
          )}
        </Box>
      </SidePanel>

      <ChatBox>
        <MessageList>
          {messages.map((msg) => (
            <Box key={msg.id} display="flex" flexDirection={msg.sender === "User" ? "row-reverse" : "row"} alignItems="flex-start" gap={2}>
              <Avatar sx={{ bgcolor: msg.sender === "User" ? "#286BEF1A" : "#303f9f", width: 28, height: 28, fontSize: 14 }}>{msg.sender === "User" ? "AS" : <AutoFixHigh />}</Avatar>
              <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: msg.sender === "User" ? "#E6F0FD" : "#fff", border: msg.sender === "AI" ? "1px solid #E2E8F0" : "none" }}>
                {msg.attachments?.map((file, idx) => (
                  <Chip key={idx} icon={file.type.startsWith("image/") ? <Image /> : <InsertDriveFile />} label={file.name} sx={{ m: 0.5, fontSize: "0.75rem" }} />
                ))}
                <Typography fontSize="0.95rem" color="#1E293B" whiteSpace="pre-wrap">{msg.text}</Typography>
                <Typography fontSize="0.75rem" color="#64748B" mt={0.5}>{msg.timestamp}</Typography>
              </Box>
            </Box>
          ))}
          <div ref={messageEndRef} />
        </MessageList>

        <ChatInputContainer>
          <TextField
            fullWidth
            variant="standard"
            placeholder="Write your response here..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            InputProps={{ disableUnderline: true }}
          />
          <IconButton color="primary" onClick={handleSendMessage} disabled={!inputValue && files.length === 0}><Send fontSize="small" /></IconButton>
        </ChatInputContainer>

        <Button onClick={handleSubmitChat} ref={submitBtnRef} style={{ display: "none" }}>Save Job Bar</Button>
      </ChatBox>
    </Container>
  );
};

export default AIChat;